import { URLS, createJurisdictionHeaders } from '@/data';
import { HapticFeedbackTypes, triggerHapticFeedback } from '@/utils/haptic-feedback';
import { logger } from '@/utils/logging';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const LOG_TAG = '[useCasinoFavorites]:';

export const casinoFavoritesKeys = {
    all: ['casino-favorites'] as const,
    gameIds: () => [...casinoFavoritesKeys.all, 'game-ids'] as const,
    status: (gameId: string) => [...casinoFavoritesKeys.all, 'status', gameId] as const,
};

type CasinoFavoritesResponse = {
    data: {
        favourites: Array<{
            game_id: string;
            favourited_at: string;
        }>;
        pagination?: unknown;
    };
};

type CasinoFavoriteStatusResponse = {
    data: {
        game_id: string;
        is_favourited: boolean;
    };
};

const getCasinoFavorites = async (): Promise<string[]> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/casino/favourites`, {
        method: 'GET',
        headers: {
            ...createJurisdictionHeaders(),
            JURISDICTION: 'CASH-CASINO',
            'Content-Type': 'application/json',
        },
    });

    const data = (await response.json()) as CasinoFavoritesResponse;

    if (!response.ok) {
        throw data;
    }

    return data?.data?.favourites?.map(favorite => favorite.game_id) ?? [];
};

const getCasinoFavoriteStatus = async (gameId: string): Promise<CasinoFavoriteStatusResponse> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/casino/favourites/${encodeURIComponent(gameId)}`, {
        method: 'GET',
        headers: {
            ...createJurisdictionHeaders(),
            JURISDICTION: 'CASH-CASINO',
            'Content-Type': 'application/json',
        },
    });

    const data = (await response.json()) as CasinoFavoriteStatusResponse;

    if (!response.ok) {
        throw data;
    }

    return data;
};

const addCasinoFavorite = async (gameId: string) => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/casino/favourites`, {
        method: 'POST',
        headers: {
            ...createJurisdictionHeaders(),
            JURISDICTION: 'CASH-CASINO',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ game_id: gameId }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw data;
    }

    return data;
};

const removeCasinoFavorite = async (gameId: string) => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/casino/favourites/${encodeURIComponent(gameId)}`, {
        method: 'DELETE',
        headers: {
            ...createJurisdictionHeaders(),
            JURISDICTION: 'CASH-CASINO',
            'Content-Type': 'application/json',
        },
    });

    const data = await response.json();

    if (!response.ok) {
        throw data;
    }

    return data;
};

export const useCasinoFavoriteGameIds = () => {
    return useQuery({
        queryKey: casinoFavoritesKeys.gameIds(),
        queryFn: getCasinoFavorites,
        staleTime: Infinity,
        gcTime: Infinity,
    });
};

export const useCasinoFavoriteStatus = (gameId: string) => {
    return useQuery({
        queryKey: casinoFavoritesKeys.status(gameId),
        queryFn: () => getCasinoFavoriteStatus(gameId),
        enabled: Boolean(gameId),
    });
};

export const useCasinoFavoriteActions = () => {
    const queryClient = useQueryClient();

    const cancelFavoriteQueries = (gameId: string) =>
        Promise.all([
            queryClient.cancelQueries({ queryKey: casinoFavoritesKeys.gameIds() }),
            queryClient.cancelQueries({ queryKey: casinoFavoritesKeys.status(gameId) }),
        ]);

    const invalidateFavoriteQueries = (gameId: string) => {
        queryClient.invalidateQueries({ queryKey: casinoFavoritesKeys.gameIds() });
        queryClient.invalidateQueries({ queryKey: casinoFavoritesKeys.status(gameId) });
    };

    const addFavoriteMutation = useMutation({
        mutationFn: addCasinoFavorite,
        onMutate: async favoriteGameId => {
            await cancelFavoriteQueries(favoriteGameId);

            const previousFavorites = queryClient.getQueryData<string[]>(casinoFavoritesKeys.gameIds()) ?? [];
            const previousFavoriteStatus = queryClient.getQueryData<CasinoFavoriteStatusResponse>(
                casinoFavoritesKeys.status(favoriteGameId)
            );

            queryClient.setQueryData<string[]>(casinoFavoritesKeys.gameIds(), currentFavorites => {
                const nextFavorites = currentFavorites ?? [];
                return nextFavorites.includes(favoriteGameId) ? nextFavorites : [favoriteGameId, ...nextFavorites];
            });

            queryClient.setQueryData<CasinoFavoriteStatusResponse>(casinoFavoritesKeys.status(favoriteGameId), {
                data: {
                    game_id: favoriteGameId,
                    is_favourited: true,
                },
            });

            return { previousFavorites, previousFavoriteStatus };
        },
        onError: (_error, favoriteGameId, context) => {
            queryClient.setQueryData(casinoFavoritesKeys.gameIds(), context?.previousFavorites ?? []);
            queryClient.setQueryData<CasinoFavoriteStatusResponse>(
                casinoFavoritesKeys.status(favoriteGameId),
                context?.previousFavoriteStatus ?? { data: { game_id: favoriteGameId, is_favourited: false } }
            );
        },
        onSettled: (_data, _error, favoriteGameId) => invalidateFavoriteQueries(favoriteGameId),
    });

    const removeFavoriteMutation = useMutation({
        mutationFn: removeCasinoFavorite,
        onMutate: async favoriteGameId => {
            await cancelFavoriteQueries(favoriteGameId);

            const previousFavorites = queryClient.getQueryData<string[]>(casinoFavoritesKeys.gameIds()) ?? [];
            const previousFavoriteStatus = queryClient.getQueryData<CasinoFavoriteStatusResponse>(
                casinoFavoritesKeys.status(favoriteGameId)
            );

            queryClient.setQueryData<string[]>(casinoFavoritesKeys.gameIds(), currentFavorites => {
                const nextFavorites = currentFavorites ?? [];
                return nextFavorites.filter(currentFavoriteGameId => currentFavoriteGameId !== favoriteGameId);
            });

            queryClient.setQueryData<CasinoFavoriteStatusResponse>(casinoFavoritesKeys.status(favoriteGameId), {
                data: {
                    game_id: favoriteGameId,
                    is_favourited: false,
                },
            });

            return { previousFavorites, previousFavoriteStatus };
        },
        onError: (_error, favoriteGameId, context) => {
            queryClient.setQueryData(casinoFavoritesKeys.gameIds(), context?.previousFavorites ?? []);
            queryClient.setQueryData<CasinoFavoriteStatusResponse>(
                casinoFavoritesKeys.status(favoriteGameId),
                context?.previousFavoriteStatus ?? { data: { game_id: favoriteGameId, is_favourited: true } }
            );
        },
        onSettled: (_data, _error, favoriteGameId) => invalidateFavoriteQueries(favoriteGameId),
    });

    return {
        addFavorite: addFavoriteMutation.mutateAsync,
        removeFavorite: removeFavoriteMutation.mutateAsync,
        isPending: addFavoriteMutation.isPending || removeFavoriteMutation.isPending,
    };
};

export const useCasinoGameFavorite = (gameId: string) => {
    const { data: favoriteGameIds = [] } = useCasinoFavoriteGameIds();
    const { data: favoriteStatusData, isPending: isFavoriteStatusPending } = useCasinoFavoriteStatus(gameId);
    const { addFavorite, removeFavorite, isPending: isMutating } = useCasinoFavoriteActions();
    const isFavorite = favoriteStatusData?.data.is_favourited ?? favoriteGameIds.includes(gameId);
    const isUpdating = isMutating || isFavoriteStatusPending;

    const toggleFavorite = async () => {
        if (isUpdating) {
            return;
        }

        try {
            if (isFavorite) {
                await removeFavorite(gameId);
            } else {
                await addFavorite(gameId);
            }
            triggerHapticFeedback(HapticFeedbackTypes.notificationSuccess);
        } catch (error) {
            logger.error(LOG_TAG, 'Error updating favorite status', error);
        }
    };

    return { isFavorite, isUpdating, toggleFavorite };
};
